import { WalletAdapterName } from '@/contexts/WalletContext';
import { IconBadge } from '@/pages/XDao/ProjectsPage/utils/iconBadge';
import { Wallet } from '@/services/queryOptions/walletQueryOptions';
import { NFT } from '@/types/xdao';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

type State = {
    imageName: string | null;
    imageRarity: IconBadge | null;
    showBadgeModal: boolean;
    showImageModal: boolean;
    imageUrl: string | null;
    walletMissMatch: {
        show: boolean;
        primaryWallet?: Wallet;
        connectedWallet?: {
            address: string;
            adapter: WalletAdapterName; 
        };
    }
    mintingTrait: {
        show: boolean;
        shouldMint: boolean;
        error: string | null;
    };
    mintingEgg: {
        show: boolean;
        shouldMint: boolean;
        error: string | null;
    };
    mintingNft: {
        show: boolean;
        shouldMint: boolean;
        error: string | null;
        selectedNft: NFT | null;
        quantity: number;
        projectId: number | null;
    };
    upgradingNft: {
        show: boolean;
        shouldUpgrade: boolean;
        error: string | null;
    };
    claimingNft: {
        show: boolean;
        shouldClaim: boolean;
        error: string | null;
        ownershipId: number | null;
        nftName: string | null;
        nftImage: string | null;
    };
    claimConfirmation: {
        show: boolean;
        ownershipId: number | null;
        nftName: string | null;
        nftImage: string | null;
        thumbnail: string | null;
        description: string | null;
        rarityName: string | null;
        lockTokenName: string | null;
        lockTokenAmount: string | null;
        lockTokenImage: string | null;
    };
    codeVerification: {
        show: boolean;
        onVerified?: () => void;
    };
    login: {
        show: boolean;
    };
    accountManagement: {
        show: boolean;
    };
    accountExists: {
        show: boolean;
        provider: 'x' | 'google' | null;
        email: string | null;
        existingProvider: 'x' | 'google' | null;
        existingUserId?: number;
        suggestedAction?: string;
    };
    swapConfirmation: {
        show: boolean;
        swapData: {
            inputMint: string;
            outputMint: string;
            outputAmount: string;
            tokenName: string;
            decimals: number;
        } | null;
        tokenName: string | null;
        nftName: string | null;
        nftImage: string | null;
        quantity: number;
        onConfirm: (() => void) | null;
        onCancel: (() => void) | null;
    };
    // Add more modals here as needed
    // showConfirmModal: boolean;
    // showWalletModal: boolean;
}

type Action = {
    setShowBadgeModal: (show: boolean) => void;
    setShowImageModal: (show: boolean, imageUrl?: string, imageName?: string, imageRarity?: IconBadge) => void;
    setWalletMismatchModal: (show: State['walletMissMatch']) => void;
    setMintingTrait: (state: { show?: boolean; shouldMint?: boolean; error?: string | null }) => void;
    setMintingEgg: (state: { show?: boolean; shouldMint?: boolean; error?: string | null }) => void;
    setMintingNft: (state: { show?: boolean; shouldMint?: boolean; error?: string | null; selectedNft?: NFT | null; quantity?: number; projectId?: number | null }) => void;
    setUpgradingNft: (state: { show?: boolean; shouldUpgrade?: boolean; error?: string | null }) => void;
    setClaimingNft: (state: { show?: boolean; shouldClaim?: boolean; error?: string | null; ownershipId?: number | null; nftName?: string | null; nftImage?: string | null }) => void;
    setClaimConfirmation: (state: { show?: boolean; ownershipId?: number | null; nftName?: string | null; nftImage?: string | null; thumbnail?: string | null; description?: string | null; rarityName?: string | null; lockTokenName?: string | null; lockTokenAmount?: string | null; lockTokenImage?: string | null }) => void;
    setCodeVerification: (state: { show?: boolean; onVerified?: () => void }) => void;
    setLoginModal: (state: { show?: boolean }) => void;
    setAccountManagementModal: (state: { show?: boolean }) => void;
    setAccountExistsModal: (state: {
        show?: boolean;
        provider?: 'x' | 'google' | null;
        email?: string | null;
        existingProvider?: 'x' | 'google' | null;
        existingUserId?: number;
        suggestedAction?: string;
    }) => void;
    setSwapConfirmation: (state: {
        show?: boolean;
        swapData?: {
            inputMint: string;
            outputMint: string;
            outputAmount: string;
            tokenName: string;
            decimals: number;
        } | null;
        tokenName?: string | null;
        nftName?: string | null;
        nftImage?: string | null;
        quantity?: number;
        onConfirm?: (() => void) | null;
        onCancel?: (() => void) | null;
    }) => void;
    // Add more modal actions here as needed
    closeAllModals: () => void;
}

const useModalStore = create<State & Action>()(
    immer((set) => ({
        // Initial state
        imageName: null,
        imageRarity: null,
        showBadgeModal: false,
        showImageModal: false,
        imageUrl: null,
        walletMissMatch: {
            show: false,
            primaryWallet: undefined,
            connectedWallet: undefined,
        },
        mintingTrait: {
            show: false,
            shouldMint: false,
            error: null,
        },
        mintingEgg: {
            show: false,
            shouldMint: false,
            error: null,
        },
        mintingNft: {
            show: false,
            shouldMint: false,
            error: null,
            selectedNft: null,
            quantity: 1,
            projectId: null,
        },
        upgradingNft: {
            show: false,
            shouldUpgrade: false,
            error: null,
        },
        codeVerification: {
            show: false,
            onVerified: undefined,
        },
        claimingNft: {
            show: false,
            shouldClaim: false,
            error: null,
            ownershipId: null,
            nftName: null,
            nftImage: null,
        },
        claimConfirmation: {
            show: false,
            ownershipId: null,
            nftName: null,
            nftImage: null,
            thumbnail: null,
            description: null,
            rarityName: null,
            lockTokenName: null,
            lockTokenAmount: null,
            lockTokenImage: null,
        },
        login: {
            show: false,
        },
        accountManagement: {
            show: false,
        },
        accountExists: {
            show: false,
            provider: null,
            email: null,
            existingProvider: null,
            existingUserId: undefined,
            suggestedAction: undefined,
        },
        swapConfirmation: {
            show: false,
            swapData: null,
            tokenName: null,
            nftName: null,
            nftImage: null,
            quantity: 1,
            onConfirm: null,
            onCancel: null,
        },

        // Actions
        setShowBadgeModal: (show) => set({ showBadgeModal: show }),

        setShowImageModal: (show, imageUrl, imageName, imageRarity) => set((state) => ({
            showImageModal: show,
            // Keep imageUrl when closing to allow exit animation, only update when opening
            imageUrl: show ? (imageUrl || state.imageUrl) : state.imageUrl,
            imageName: show ? (imageName || state.imageName) : state.imageName,
            imageRarity: show ? (imageRarity || state.imageRarity) : state.imageRarity,
        })),

        setWalletMismatchModal: (walletMissMatch: State['walletMissMatch']) => set({
            walletMissMatch
        }),

        setMintingTrait: (state) => set((draft) => {
            if (state.show !== undefined) draft.mintingTrait.show = state.show;
            if (state.shouldMint !== undefined) draft.mintingTrait.shouldMint = state.shouldMint;
            if (state.error !== undefined) draft.mintingTrait.error = state.error;
        }),

        setMintingEgg: (state) => set((draft) => {
            if (state.show !== undefined) draft.mintingEgg.show = state.show;
            if (state.shouldMint !== undefined) draft.mintingEgg.shouldMint = state.shouldMint;
            if (state.error !== undefined) draft.mintingEgg.error = state.error;
        }),

        setMintingNft: (state) => set((draft) => {
            if (state.show !== undefined) draft.mintingNft.show = state.show;
            if (state.shouldMint !== undefined) draft.mintingNft.shouldMint = state.shouldMint;
            if (state.error !== undefined) draft.mintingNft.error = state.error;
            if (state.selectedNft !== undefined) draft.mintingNft.selectedNft = state.selectedNft;
            if (state.quantity !== undefined) draft.mintingNft.quantity = state.quantity;
            if (state.projectId !== undefined) draft.mintingNft.projectId = state.projectId;
        }),

        setUpgradingNft: (state) => set((draft) => {
            if (state.show !== undefined) draft.upgradingNft.show = state.show;
            if (state.shouldUpgrade !== undefined) draft.upgradingNft.shouldUpgrade = state.shouldUpgrade;
            if (state.error !== undefined) draft.upgradingNft.error = state.error;
        }),

        setCodeVerification: (state) => set((draft) => {
            if (state.show !== undefined) draft.codeVerification.show = state.show;
            if (state.onVerified !== undefined) draft.codeVerification.onVerified = state.onVerified;
        }),

        setClaimingNft: (state) => set((draft) => {
            if (state.show !== undefined) draft.claimingNft.show = state.show;
            if (state.shouldClaim !== undefined) draft.claimingNft.shouldClaim = state.shouldClaim;
            if (state.error !== undefined) draft.claimingNft.error = state.error;
            if (state.ownershipId !== undefined) draft.claimingNft.ownershipId = state.ownershipId;
            if (state.nftName !== undefined) draft.claimingNft.nftName = state.nftName;
            if (state.nftImage !== undefined) draft.claimingNft.nftImage = state.nftImage;
        }),

        setClaimConfirmation: (state) => set((draft) => {
            if (state.show !== undefined) draft.claimConfirmation.show = state.show;
            if (state.ownershipId !== undefined) draft.claimConfirmation.ownershipId = state.ownershipId;
            if (state.nftName !== undefined) draft.claimConfirmation.nftName = state.nftName;
            if (state.nftImage !== undefined) draft.claimConfirmation.nftImage = state.nftImage;
            if (state.thumbnail !== undefined) draft.claimConfirmation.thumbnail = state.thumbnail;
            if (state.description !== undefined) draft.claimConfirmation.description = state.description;
            if (state.rarityName !== undefined) draft.claimConfirmation.rarityName = state.rarityName;
            if (state.lockTokenName !== undefined) draft.claimConfirmation.lockTokenName = state.lockTokenName;
            if (state.lockTokenAmount !== undefined) draft.claimConfirmation.lockTokenAmount = state.lockTokenAmount;
            if (state.lockTokenImage !== undefined) draft.claimConfirmation.lockTokenImage = state.lockTokenImage;
        }),

        setLoginModal: (state) => set((draft) => {
            if (state.show !== undefined) draft.login.show = state.show;
        }),

        setAccountManagementModal: (state) => set((draft) => {
            if (state.show !== undefined) draft.accountManagement.show = state.show;
        }),

        setAccountExistsModal: (state) => set((draft) => {
            if (state.show !== undefined) draft.accountExists.show = state.show;
            if (state.provider !== undefined) draft.accountExists.provider = state.provider;
            if (state.email !== undefined) draft.accountExists.email = state.email;
            if (state.existingProvider !== undefined) draft.accountExists.existingProvider = state.existingProvider;
            if (state.existingUserId !== undefined) draft.accountExists.existingUserId = state.existingUserId;
            if (state.suggestedAction !== undefined) draft.accountExists.suggestedAction = state.suggestedAction;
        }),

        setSwapConfirmation: (state) => set((draft) => {
            if (state.show !== undefined) draft.swapConfirmation.show = state.show;
            if (state.swapData !== undefined) draft.swapConfirmation.swapData = state.swapData;
            if (state.tokenName !== undefined) draft.swapConfirmation.tokenName = state.tokenName;
            if (state.nftName !== undefined) draft.swapConfirmation.nftName = state.nftName;
            if (state.nftImage !== undefined) draft.swapConfirmation.nftImage = state.nftImage;
            if (state.quantity !== undefined) draft.swapConfirmation.quantity = state.quantity;
            if (state.onConfirm !== undefined) draft.swapConfirmation.onConfirm = state.onConfirm;
            if (state.onCancel !== undefined) draft.swapConfirmation.onCancel = state.onCancel;
        }),

        closeAllModals: () => set({
            showBadgeModal: false,
            showImageModal: false,
            imageUrl: null,
            imageName: null,
            walletMissMatch: {
                show: false,
                primaryWallet: undefined,
                connectedWallet: undefined,
            },
            mintingTrait: {
                show: false,
                shouldMint: false,
                error: null,
            },
            mintingEgg: {
                show: false,
                shouldMint: false,
                error: null,
            },
            mintingNft: {
                show: false,
                shouldMint: false,
                error: null,
                selectedNft: null,
                quantity: 1,
                projectId: null,
            },
            upgradingNft: {
                show: false,
                shouldUpgrade: false,
                error: null,
            },
            codeVerification: {
                show: false,
                onVerified: undefined,
            },
            claimingNft: {
                show: false,
                shouldClaim: false,
                error: null,
                ownershipId: null,
                nftName: null,
                nftImage: null,
            },
            claimConfirmation: {
                show: false,
                ownershipId: null,
                nftName: null,
                nftImage: null,
                thumbnail: null,
                description: null,
                rarityName: null,
                lockTokenName: null,
                lockTokenAmount: null,
                lockTokenImage: null,
            },
            login: {
                show: false,
            },
            accountManagement: {
                show: false,
            },
            accountExists: {
                show: false,
                provider: null,
                email: null,
                existingProvider: null,
                existingUserId: undefined,
                suggestedAction: undefined,
            },
            swapConfirmation: {
                show: false,
                swapData: null,
                tokenName: null,
                nftName: null,
                nftImage: null,
                quantity: 1,
                onConfirm: null,
                onCancel: null,
            },
            // Add more modals here when added
        }),
    }))
);

export default useModalStore;
